import { Injectable, inject } from '@angular/core';
import { CrudService, type UpdateOptions } from '@context/frontend/api-client';
import { StorageService } from '@context/frontend/storage';
import { Organization } from '@context/shared/types/organization';
import { firstValueFrom } from 'rxjs';

/**
 * Organization service for the admin console (super admins). Does not depend on
 * AuthService or organization context from auth—all organization-scoped API
 * calls accept an explicit organizationId. Use this in the admin project instead
 * of OrganizationService to avoid coupling to AuthService.
 */
@Injectable({ providedIn: 'root' })
export class AdminOrganizationService extends CrudService<Organization> {
	private readonly storageService = inject(StorageService);

	static readonly CollectionId = 'organizations';

	constructor() {
		super(AdminOrganizationService.CollectionId, {
			useOrganization: false,
			version: '1',
		});
	}

	/**
	 * Uploads a logo image for an organization and returns the storage path to apply.
	 * Does not refresh any auth context.
	 */
	async updateLogo(
		organizationData: Organization | null,
		newOrganizationData: Partial<Organization>,
		file: File | null,
	): Promise<{ url: string | null; updated: boolean }> {
		const previousUrl = organizationData?.logoUrl;

		let deleteLogo = !newOrganizationData.logoUrl && !!previousUrl;
		const hasLogoChanged = file || deleteLogo;
		if (!hasLogoChanged) return { url: null, updated: false };

		let url: string | null = null;
		if (file) {
			url = await this.storageService
				.uploadFile(file, { entityType: 'details' })
				.catch((error) => {
					console.error(error);
					throw new Error(
						'AdminOrganizationService.updateLogo: There was an issue uploading the file',
					);
				})
				.then((result) =>
					this.storageService.getDownloadUrl(result.ref),
				);
			deleteLogo = true;
		}

		if (previousUrl && deleteLogo) {
			this.storageService.deleteFromURL(previousUrl).catch((error) => {
				console.error(
					'AdminOrganizationService.updateLogo: There was an issue removing the old image from:',
					previousUrl,
					error,
				);
			});
		}

		return { url, updated: true };
	}

	/**
	 * Updates an organization. Does not refresh AuthService (admin has no org context).
	 */
	override readonly update = (
		payload: Partial<Organization & { id: string }>,
		options: UpdateOptions,
	) => super.update(payload, options);

	/**
	 * Creates a new organization and invites an owner. Requires super admin auth.
	 */
	readonly createOrganizationWithOwner = (payload: {
		name: string;
		logoUrl?: string | null;
		ownerEmail: string;
		ownerGivenName?: string;
		ownerFamilyName?: string;
		ownerMessage?: string;
	}) =>
		firstValueFrom(
			this.http.post<{ organizationId: string }>(
				`${this.api}/v1/organizations/create`,
				payload,
			),
		);
}
